import React, { useState, useEffect, useCallback } from 'react';
import CustomDatePickerComponent from '../shared/CustomDatePicker';
import {
    TrendingUp, DollarSign, ShoppingCart, Users, Package, UserCheck,
    RefreshCw, AlertTriangle, BarChart3, ArrowUpRight, Target,
    Truck, ChevronDown, ChevronUp, AlertCircle, Calendar, Star
} from 'lucide-react';
import { getAnalyticsSummary } from '../../../../services/api/analyticsApi';
import { ScoreCard } from './analyticsShared';

// ─── Utilities ──────────────────────────────────────────────────────────────

function formatCurrency(value) {
    if (value == null || value === '') return 'AED 0';
    return new Intl.NumberFormat('en-AE', {
        style: 'currency', currency: 'AED', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(Number(value) || 0);
}

function formatNumber(value) {
    if (value == null) return '0';
    return new Intl.NumberFormat('en-AE').format(Number(value) || 0);
}

function getAvatarGradient(name = '') {
    const palette = [
        'from-violet-500 to-purple-600', 'from-blue-500 to-cyan-500',
        'from-emerald-500 to-teal-500', 'from-orange-500 to-amber-500',
        'from-rose-500 to-pink-500', 'from-sky-500 to-indigo-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
}

/** Build date range from preset filter */
function buildDateRange(filter) {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);

    let startDate;
    if (filter === 'today') {
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
    } else if (filter === 'last7') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
    } else {
        return null; // custom — caller handles
    }
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const SkeletonBlock = ({ className = '' }) => (
    <div className={`bg-[var(--color-border)] animate-pulse rounded-xl ${className}`} />
);

/** Section container */
const Section = ({ title, icon: Icon, iconColor = 'text-blue-500', children, action }) => (
    <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[var(--color-border-soft)] flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Icon size={15} className={iconColor} />
                <h2 className="text-sm font-bold text-[var(--color-text-primary)]">{title}</h2>
            </div>
            {action}
        </div>
        <div className="p-4">{children}</div>
    </div>
);

/** Order Status Funnel — horizontal bar */
const StatusFunnel = ({ funnel, loading }) => {
    const colorMap = {
        DELIVERED: 'bg-emerald-500', CONFIRMED: 'bg-blue-500',
        SUBMITTED: 'bg-indigo-400', NEW: 'bg-sky-500', NEW_ORDER: 'bg-sky-500',
        ACKNOWLEDGED: 'bg-violet-400', SENT_TO_WAREHOUSE: 'bg-orange-400',
        IN_WAREHOUSE: 'bg-orange-500', DISPATCHED: 'bg-cyan-500',
        OUT_FOR_DELIVERY: 'bg-teal-500', CANCELLED: 'bg-rose-500',
        REJECTED: 'bg-red-600', RETURNED: 'bg-amber-500',
    };

    if (loading) {
        return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-8" />)}</div>;
    }

    if (!funnel?.length) {
        return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No order data in this period.</p>;
    }

    const totalCount = funnel.reduce((s, f) => s + (f.count || 0), 0);
    const sorted = [...funnel].sort((a, b) => (b.count || 0) - (a.count || 0));

    return (
        <div className="space-y-2.5">
            {sorted.map((item) => {
                const pct = totalCount > 0 ? ((item.count || 0) / totalCount) * 100 : 0;
                const color = colorMap[item.statusCode?.toUpperCase()] || 'bg-gray-400';
                return (
                    <div key={item.statusCode} className="flex items-center gap-3">
                        <span className="text-[11px] font-bold text-[var(--color-text-muted)] w-28 shrink-0 truncate capitalize">
                            {item.statusCode?.replace(/_/g, ' ').toLowerCase() ?? '—'}
                        </span>
                        <div className="flex-1 h-5 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                            <div
                                className={`h-full ${color} rounded-full transition-all duration-700 flex items-center pl-2`}
                                style={{ width: `${Math.max(pct, 4)}%` }}
                            >
                                {pct > 15 && (
                                    <span className="text-[9px] font-black text-white">{formatNumber(item.count)}</span>
                                )}
                            </div>
                        </div>
                        {pct <= 15 && (
                            <span className="text-[11px] font-bold text-[var(--color-text-primary)] w-8 text-right shrink-0">
                                {formatNumber(item.count)}
                            </span>
                        )}
                        <span className="text-[10px] text-[var(--color-text-muted)] w-10 text-right shrink-0">
                            {pct.toFixed(1)}%
                        </span>
                    </div>
                );
            })}
            <div className="pt-2 border-t border-[var(--color-border-soft)] flex items-center justify-between">
                <span className="text-[11px] text-[var(--color-text-muted)] font-medium">Total orders in period</span>
                <span className="text-sm font-black text-[var(--color-text-primary)]">{formatNumber(totalCount)}</span>
            </div>
        </div>
    );
};

/** Top Customers table */
const TopCustomersTable = ({ customers, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-10" />)}</div>;
    if (!customers?.length) return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No customer data in this period.</p>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[var(--color-border-soft)]">
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">#</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Customer</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-center">Orders</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-right">Revenue</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]">
                    {customers.map((c, idx) => (
                        <tr key={c.publicId || idx} className="hover:bg-[var(--color-bg-hover)] transition-colors">
                            <td className="py-2.5 pr-2">
                                <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-[var(--color-text-muted)]'}`}>
                                    {idx + 1}
                                </span>
                            </td>
                            <td className="py-2.5 min-w-0 pr-2">
                                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br ${getAvatarGradient(c.name)} flex items-center justify-center text-white text-[9px] sm:text-[10px] font-black shrink-0`}>
                                        {c.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <span className="text-xs sm:text-sm font-semibold text-[var(--color-text-primary)] truncate max-w-[90px] min-[380px]:max-w-[130px] sm:max-w-[180px]" title={c.name}>{c.name}</span>
                                </div>
                            </td>
                            <td className="py-2.5 text-center px-1">
                                <span className="text-[11px] sm:text-xs font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">{c.orderCount}</span>
                            </td>
                            <td className="py-2.5 text-right pl-2">
                                <span className="text-xs sm:text-sm font-black text-emerald-600 dark:text-emerald-400 tabular-nums whitespace-nowrap">{formatCurrency(c.revenue)}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Top Selling Products */
const TopProductsTable = ({ products, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <SkeletonBlock key={i} className="h-10" />)}</div>;
    if (!products?.length) return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No product sales in this period.</p>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[var(--color-border-soft)]">
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">#</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Product</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-center">Qty Sold</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-right">Revenue</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]">
                    {products.map((p, idx) => (
                        <tr key={p.publicId} className="hover:bg-[var(--color-bg-hover)] transition-colors">
                            <td className="py-2.5 pr-3">
                                <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-[var(--color-text-muted)]'}`}>
                                    {idx + 1}
                                </span>
                            </td>
                            <td className="py-2.5">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shrink-0">
                                        <Package size={12} className="text-white" />
                                    </div>
                                    <span className="text-sm font-semibold text-[var(--color-text-primary)] truncate max-w-[140px]">{p.name}</span>
                                </div>
                            </td>
                            <td className="py-2.5 text-center">
                                <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full tabular-nums">
                                    {formatNumber(p.quantitySold)}
                                </span>
                            </td>
                            <td className="py-2.5 text-right">
                                <span className="text-sm font-black text-emerald-600 tabular-nums">{formatCurrency(p.revenue)}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Low Stock Alerts */
const LowStockAlerts = ({ products, loading }) => {
    const [expanded, setExpanded] = useState(false);

    if (loading) return <SkeletonBlock className="h-16" />;
    if (!products?.length) {
        return (
            <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Package size={14} className="text-emerald-600" />
                </div>
                <div>
                    <p className="text-sm font-bold text-emerald-800">All products well stocked</p>
                    <p className="text-[11px] text-emerald-600 font-medium">No items are below their low stock threshold.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
            <button
                onClick={() => setExpanded(e => !e)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-amber-100 transition-colors"
            >
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle size={16} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-amber-900">{products.length} Product{products.length !== 1 ? 's' : ''} Running Low</p>
                    <p className="text-[11px] text-amber-700 font-medium mt-0.5">Below configured low-stock threshold</p>
                </div>
                <span className="text-xs font-bold text-amber-700 bg-amber-200 px-2.5 py-1 rounded-full shrink-0">{products.length}</span>
                {expanded ? <ChevronUp size={16} className="text-amber-500 shrink-0" /> : <ChevronDown size={16} className="text-amber-500 shrink-0" />}
            </button>
            {expanded && (
                <div className="border-t border-amber-200 divide-y divide-amber-100">
                    {products.map(p => (
                        <div key={p.publicId} className="flex items-center gap-3 px-4 py-3 hover:bg-amber-100/50 transition-colors">
                            <div className="w-7 h-7 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
                                <Package size={13} className="text-amber-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-amber-900 truncate">{p.name}</p>
                                <p className="text-[10px] text-amber-700 font-medium">{p.itemCode}</p>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-sm font-black text-rose-600">{formatNumber(p.availableStock)}</p>
                                <p className="text-[10px] text-amber-600 font-medium">min: {formatNumber(p.lowStockThreshold)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Customers to Call — expandable list */
const CustomersToCallList = ({ customers, loading }) => {
    const [expanded, setExpanded] = useState(false);

    if (loading) return <SkeletonBlock className="h-16" />;

    if (!customers?.length) {
        return (
            <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Users size={14} className="text-emerald-600" />
                </div>
                <div>
                    <p className="text-sm font-bold text-emerald-800">All customers are active</p>
                    <p className="text-[11px] text-emerald-600 font-medium">No customers without a recent order.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl overflow-hidden">
            <button
                onClick={() => setExpanded(e => !e)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-rose-100 transition-colors"
            >
                <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                    <Users size={16} className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-rose-900">{customers.length} Customer{customers.length !== 1 ? 's' : ''} to Follow Up</p>
                    <p className="text-[11px] text-rose-700 font-medium mt-0.5">No order placed in the last 30 days</p>
                </div>
                <span className="text-xs font-bold text-rose-700 bg-rose-200 px-2.5 py-1 rounded-full shrink-0">{customers.length}</span>
                {expanded ? <ChevronUp size={16} className="text-rose-500 shrink-0" /> : <ChevronDown size={16} className="text-rose-500 shrink-0" />}
            </button>
            {expanded && (
                <div className="border-t border-rose-200 divide-y divide-rose-100">
                    {customers.map(c => (
                        <div key={c.publicId} className="flex items-center gap-3 px-4 py-3 hover:bg-rose-100/50 transition-colors">
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(c.name)} flex items-center justify-center text-white text-[10px] font-black shrink-0`}>
                                {c.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-rose-900 truncate">{c.name}</p>
                                <p className="text-[10px] text-rose-600 font-medium">
                                    {c.lastOrderDate
                                        ? `Last order ${c.daysSinceLastOrder}d ago`
                                        : 'No orders placed yet'}
                                </p>
                            </div>
                            <div className="shrink-0">
                                <span className={`text-[10px] font-black px-2 py-1 rounded-full ${c.daysSinceLastOrder > 60 || !c.lastOrderDate ? 'bg-red-100 text-red-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {c.daysSinceLastOrder > 0 ? `${c.daysSinceLastOrder}d` : 'Never'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Sales Team Performance */
const SalesTeamTable = ({ salesTeam, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(4)].map((_, i) => <SkeletonBlock key={i} className="h-10" />)}</div>;
    if (!salesTeam?.length) return <p className="text-sm text-[var(--color-text-muted)] text-center py-6">No sales data in this period.</p>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[var(--color-border-soft)]">
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">#</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Sales Person</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-center">Orders</th>
                        <th className="pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider text-right">Revenue</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border-soft)]">
                    {salesTeam.map((sp, idx) => (
                        <tr key={sp.publicId || idx} className="hover:bg-[var(--color-bg-hover)] transition-colors">
                            <td className="py-2.5 pr-3">
                                <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-[var(--color-text-muted)]'}`}>
                                    {idx + 1}
                                </span>
                            </td>
                            <td className="py-2.5">
                                <div className="flex items-center gap-2">
                                    {sp.profilePictureUrl
                                        ? <img src={sp.profilePictureUrl} alt={sp.name} className="w-7 h-7 rounded-full object-cover shrink-0" />
                                        : <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarGradient(sp.name)} flex items-center justify-center text-white text-[10px] font-black shrink-0`}>
                                            {sp.name?.charAt(0)?.toUpperCase()}
                                          </div>
                                    }
                                    <span className="text-sm font-semibold text-[var(--color-text-primary)] truncate max-w-[120px]">{sp.name}</span>
                                </div>
                            </td>
                            <td className="py-2.5 text-center">
                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full tabular-nums">{sp.ordersHandled}</span>
                            </td>
                            <td className="py-2.5 text-right">
                                <span className="text-sm font-black text-emerald-600 tabular-nums">{formatCurrency(sp.revenueGenerated)}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Date Range Picker (custom) ──────────────────────────────────────────────

const CustomDatePicker = ({ customStart, customEnd, onStartChange, onEndChange }) => (
    <CustomDatePickerComponent
        mode="range"
        startDate={customStart}
        endDate={customEnd}
        onStartChange={onStartChange}
        onEndChange={onEndChange}
    />
);

// ─── Main AnalyticsView Component ─────────────────────────────────────────────

const AnalyticsView = () => {
    const [filter, setFilter] = useState('today');
    const [customStart, setCustomStart] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 29);
        return d.toISOString().split('T')[0];
    });
    const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().split('T')[0]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let startDate, endDate;
            if (filter === 'custom') {
                startDate = new Date(customStart + 'T00:00:00Z').toISOString();
                endDate   = new Date(customEnd   + 'T23:59:59Z').toISOString();
            } else {
                const range = buildDateRange(filter);
                startDate = range.startDate;
                endDate   = range.endDate;
            }
            const result = await getAnalyticsSummary(startDate, endDate);
            setData(result);
        } catch (err) {
            console.error('Analytics fetch failed:', err);
            setError(err?.response?.data?.message || 'Failed to load analytics. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [filter, customStart, customEnd]);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    const filterOptions = [
        { id: 'today', label: 'Today' },
        { id: 'last7', label: 'Last 7 Days' },
        { id: 'custom', label: 'Date Range' },
    ];

    const rejectionRate = data?.operations?.orderRejectionRate ?? null;

    return (
        <div className="flex flex-col min-h-0 flex-1 overflow-y-auto custom-scrollbar">
            <div className="flex flex-col gap-4 max-w-[1280px] mx-auto w-full p-4 md:p-6 pb-10">

                {/* ── HEADER ──────────────────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between flex-shrink-0">
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-[var(--color-text-primary)] flex items-center gap-2">
                            <BarChart3 size={22} className="text-blue-500" />
                            Analytics
                        </h1>
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5 font-medium">
                            Business insights based on delivered orders
                        </p>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Filter Tabs */}
                        <div className="flex bg-[var(--color-bg-hover)] rounded-xl p-1 border border-[var(--color-border)] gap-1">
                            {filterOptions.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setFilter(opt.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === opt.id
                                        ? 'bg-[var(--color-bg-card)] text-blue-600 shadow-sm border border-[var(--color-border)]'
                                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {/* Refresh */}
                        <button
                            onClick={fetchAnalytics}
                            disabled={loading}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm disabled:opacity-50"
                        >
                            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Date Range Picker (shown only when custom is active) */}
                {filter === 'custom' && (
                    <div className="flex-shrink-0 animate-fade-in">
                        <CustomDatePicker
                            customStart={customStart}
                            customEnd={customEnd}
                            onStartChange={setCustomStart}
                            onEndChange={setCustomEnd}
                        />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-medium flex-shrink-0">
                        <AlertCircle size={16} className="shrink-0 text-rose-500" />
                        {error}
                    </div>
                )}

                {/* ── SCORECARD ROW ──────────────────────────────────────────────── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                    <ScoreCard
                        title="TOTAL REVENUE"
                        value={formatCurrency(data?.revenue?.totalRevenue)}
                        subtitle="Delivered orders only"
                        icon={DollarSign}
                        gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                        loading={loading}
                    />
                    <ScoreCard
                        title="AVG. ORDER VALUE"
                        value={formatCurrency(data?.revenue?.averageOrderValue)}
                        subtitle="Per delivered order"
                        icon={TrendingUp}
                        gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                        loading={loading}
                    />
                    <ScoreCard
                        title="REJECTION RATE"
                        value={rejectionRate !== null ? `${Number(rejectionRate).toFixed(1)}%` : '—'}
                        subtitle="Rejected + cancelled"
                        icon={Target}
                        gradient={rejectionRate > 10 ? 'bg-gradient-to-br from-rose-500 to-red-600' : 'bg-gradient-to-br from-amber-400 to-orange-500'}
                        loading={loading}
                    />
                    <ScoreCard
                        title="UPCOMING DELIVERIES"
                        value={data?.operations?.upcomingDeliveriesCount != null ? formatNumber(data?.operations?.upcomingDeliveriesCount) : '—'}
                        subtitle="Active orders scheduled"
                        icon={Truck}
                        gradient="bg-gradient-to-br from-cyan-500 to-sky-600"
                        loading={loading}
                    />
                </div>



                {/* ── TWO COLUMN: CUSTOMERS + PRODUCTS ─────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Section
                        title="Top Customers by Revenue"
                        icon={Users}
                        iconColor="text-violet-500"
                        action={
                            <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                                Top 10
                            </span>
                        }
                    >
                        <TopCustomersTable customers={data?.customers?.topCustomers} loading={loading} />
                    </Section>

                    <Section
                        title="Top Selling Products"
                        icon={Star}
                        iconColor="text-amber-500"
                        action={
                            <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                                By Qty
                            </span>
                        }
                    >
                        <TopProductsTable products={data?.warehouse?.topSellingProducts} loading={loading} />
                    </Section>
                </div>

                {/* ── CUSTOMERS TO CALL ─────────────────────────────────────────── */}
                <Section
                    title="Customers to Follow Up"
                    icon={Users}
                    iconColor="text-rose-500"
                    action={
                        <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                            No order in 30+ days (Global)
                        </span>
                    }
                >
                    <CustomersToCallList customers={data?.customers?.customersToCall} loading={loading} />
                </Section>

                {/* ── LOW STOCK ALERTS ─────────────────────────────────────────── */}
                <Section title="Warehouse — Low Stock Alerts" icon={AlertTriangle} iconColor="text-amber-500">
                    <LowStockAlerts products={data?.warehouse?.lowStockProducts} loading={loading} />
                </Section>

                {/* ── SALES TEAM PERFORMANCE ───────────────────────────────────── */}
                <Section
                    title="Sales Team Performance"
                    icon={UserCheck}
                    iconColor="text-emerald-500"
                    action={
                        <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                            Delivered only
                        </span>
                    }
                >
                    <SalesTeamTable salesTeam={data?.salesTeam} loading={loading} />
                </Section>

            </div>
        </div>
    );
};

export default AnalyticsView;
